/**
 * 🌐 支持的语言列表（与后端 LanguageNameMap 对齐）
 * label 用于菜单显示，code 发送给后端
 *
 * 共享常量：供 App.js（主界面菜单）和 AuthPage.js（登录页）共同使用
 *
 * 存储约定：
 * - localStorage.language：用户显式选择的“对话语言”，发给后端控制 Agent 回复语言
 * - localStorage.i18nextLng：仅控制 GUI 文案（中文界面或英文界面），可由 i18next 探测写入
 * 二者不得混用：未选过 language 时按钮默认显示「中文」，若 WebSocket 误用浏览器探测到的
 * i18nextLng（常为 en / en-US），会出现「菜单中文、AI 英文」的分裂状态。
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'zh-CN', label: '中文' },
  { code: 'en-US', label: 'English 英语' },
  { code: 'hi-IN', label: 'हिन्दी 印地语' },
  { code: 'es-ES', label: 'Español 西班牙语' },
  { code: 'ar-SA', label: 'العربية 阿拉伯语' },
  { code: 'fr-FR', label: 'Français 法语' },
  { code: 'bn-BD', label: 'বাংলা 孟加拉语' },
  { code: 'pt-BR', label: 'Português 葡萄牙语' },
  { code: 'ru-RU', label: 'Русский 俄语' },
  { code: 'id-ID', label: 'Bahasa Indonesia 印尼语' },
  { code: 'ur-PK', label: 'اردو 乌尔都语' },
  { code: 'de-DE', label: 'Deutsch 德语' },
  { code: 'ja-JP', label: '日本語 日语' },
  { code: 'vi-VN', label: 'Tiếng Việt 越南语' },
  { code: 'tr-TR', label: 'Türkçe 土耳其语' },
  { code: 'ko-KR', label: '한국어 韩语' },
  { code: 'th-TH', label: 'ภาษาไทย 泰语' },
  { code: 'it-IT', label: 'Italiano 意大利语' },
  { code: 'pl-PL', label: 'Polski 波兰语' },
  { code: 'nl-NL', label: 'Nederlands 荷兰语' },
  { code: 'uk-UA', label: 'Українська 乌克兰语' },
  { code: 'ms-MY', label: 'Bahasa Melayu 马来语' },
  { code: 'fa-IR', label: 'فارسی 波斯语' },
  { code: 'sw-KE', label: 'Kiswahili 斯瓦希里语' },
  { code: 'tl-PH', label: 'Filipino 菲律宾语' },
  { code: 'ro-RO', label: 'Română 罗马尼亚语' },
  { code: 'ta-IN', label: 'தமிழ் 泰米尔语' },
  { code: 'te-IN', label: 'తెలుగు 泰卢固语' },
  { code: 'mr-IN', label: 'मराठी 马拉地语' },
  { code: 'pa-PK', label: 'پنجابی 旁遮普语' },
  { code: 'yue-CN', label: '粤語 粤语' },
];

const DEFAULT_LANGUAGE = 'zh-CN';

/**
 * 用户对话语言（发给后端）。只读 language，绝不回退到 i18nextLng。
 */
export function getPreferredLanguage() {
  const code = localStorage.getItem('language');
  if (code && SUPPORTED_LANGUAGES.some((l) => l.code === code)) {
    return code;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * GUI 界面语言：仅中文用中文包，其余一律英文包。
 */
export function getGuiLanguage(langCode = getPreferredLanguage()) {
  return langCode === 'zh-CN' ? 'zh-CN' : 'en-US';
}

/**
 * 切换语言的通用逻辑（不含 WebSocket 通知）
 * @param {string} langCode - 语言代码，如 'zh-CN'
 * @param {object} i18n - i18next 实例
 */
export function switchLanguage(langCode, i18n) {
  const code = SUPPORTED_LANGUAGES.some((l) => l.code === langCode)
    ? langCode
    : DEFAULT_LANGUAGE;
  // 存储用户选择的实际语言（发给后端控制 Agent 回复语言）
  localStorage.setItem('language', code);
  // GUI 界面：中文选中文，其余全部用英文
  const guiLang = getGuiLanguage(code);
  if (i18n) {
    i18n.changeLanguage(guiLang);
  }
  localStorage.setItem('i18nextLng', guiLang);
}

/**
 * 启动时校正语言状态，消除「按钮显示中文、AI 却收英文」：
 * - 从未显式选过 language → 写入 zh-CN（与按钮默认文案一致）
 * - 已选过 → 按 language 校正 i18nextLng / i18n，避免浏览器探测残留
 */
export function ensureLanguageSynced(i18n) {
  const explicit = localStorage.getItem('language');
  if (!explicit || !SUPPORTED_LANGUAGES.some((l) => l.code === explicit)) {
    switchLanguage(DEFAULT_LANGUAGE, i18n);
    return DEFAULT_LANGUAGE;
  }
  const guiLang = getGuiLanguage(explicit);
  localStorage.setItem('i18nextLng', guiLang);
  if (i18n && i18n.language !== guiLang) {
    i18n.changeLanguage(guiLang);
  }
  return explicit;
}

/**
 * 获取当前选中的语言 label
 */
export function getCurrentLanguageLabel() {
  const code = getPreferredLanguage();
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.label || '中文';
}
